import React, { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Icon, Tooltip } from '@mui/material';
import styles from './ActionItemModal.module.scss';
import { Text } from '../typography';
import { useState } from 'react';
import { Dropdown, DropdownButton, Form } from 'react-bootstrap';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { loader } from 'graphql.macro';
import {
  priorityStringLookup,
  statusStringLookup,
} from '../action_items/action_item_utilities';
import SimpleUserCard from '../SimpleUserCard';
import { useChangeWorkspace, useQueryParams } from '../../hooks/misc';
import Button from '../Button';
import ActionItemComment from '../action_items/ActionItemComment';
import LabelSelector from '../LabelSelector';
import {
  actionItemModalVar,
  hideToast,
  showToast,
  toastVar,
} from '../../graphql/cache/modal';
import { toastLength, toastVariant } from '../../constants/misc';
import { useNavigate } from 'react-router-dom';
import { paths, searchParamKeys } from '../../constants/strings';
import { getRoute } from '../../constants/strings';
import AddAssetModal from './AddAssetModal';
import { useCurrentUser } from '../../providers/UserProvider';
import moment from 'moment';
import { getLocalTime } from '../../utilities/time';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys.js';
import { t } from 'i18next';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { useModal } from '../../providers/ModalProvider.jsx';
import { modals } from '../../providers/modals.js';
import ActionItemHistory from '../action_items/ActionItemHistory.jsx';
import QuickFileDropzone from '../image_uploads/QuickFileDropzone.jsx';
import SimpleFileCard from '../files/SimpleFileCard.jsx';
import { checkDueDateColor } from '../tables/MTableComponents.jsx';
import { useIsMobile } from '../../hooks/misc';
import { customColors } from '../../utilities/misc.js';
import { useWatcher } from '../../providers/WatcherProvider.jsx';
import TextWithIcon from '../typography/TextWithIcon.jsx';
import { useIncidentMutations } from '../../hooks/incident/incident_mutations.js';
import { useObservationMutations } from '../../hooks/observation/observation_mutations.js';
import Spacer from '../typography/Spacer.jsx';
import ActionItemChecklist from './ActionItemChecklist.jsx';
import SimpleWorkspaceCard from '../SimpleWorkspaceCard.jsx';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider.jsx';
import { useWorkspaceMutations } from '../../hooks/addUserToWorkspace.js';
import OPASDropdown from '../dropdownV2/Dropdown.jsx';
import UserMultiSelector from '../UserMultiSelector.jsx';
import UserSelector from '../UserSelector.jsx';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Toast from './Toast.jsx';
import DatePickerComponent from './DatePickerComponent.jsx';

const addActionItemLabelMutation = loader(
  './ActionItemModal.createActionItemLabel.graphql',
);
const deleteActionItemLabelMutation = loader(
  './ActionItemModal.deleteActionItemLabel.graphql',
);
const actionItemQuery = loader('./ActionItemModal.fetch.graphql');
const commentMutation = loader('./ActionItemModal.createComment.graphql');
const actionItemCreateMutation = loader(
  './ActionItemModal.createActionItem.graphql',
);

const actionItemUpdateMutation = loader(
  './ActionItemModal.updateActionItem.graphql',
);
const deleteActionItemMutation = loader(
  './ActionItemModal.deleteActionItem.graphql',
);

const removeParticipantMutation = loader(
  './ActionItemModal.removeParticipant.graphql',
);
const addParticipantMutation = loader(
  './ActionItemModal.addParticipant.graphql',
);

const addAttachmentMutation = loader('./ActionItemModal.addAttachment.graphql');
const deleteAttachmentMutation = loader(
  './ActionItemModal.deleteAttachment.graphql',
);

function Comments({
  state,
  activity,
  commentsEndRef,
  addComment,
  comment,
  user,
  setComment,
  refetch,
}) {
  const isMobile = useIsMobile();

  return (
    <div className={styles.commentBoxContainer}>
      <div className={styles.commentBox}>
        <img
          className={styles.userAvatar}
          src={user?.avatarUrl}
          alt="your avatar"
        />
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            addComment();
          }}
        >
          <Form.Control
            className={styles.commentInput}
            as={'textarea'}
            value={comment}
            placeholder={t(keys.action_items.COMMENT)}
            data-cy="input-action-item-modal-comment"
            onChange={(e) => {
              setComment(e.target.value);
            }}
          />
        </Form>
        {isMobile ? (
          <Icon
            testid="action-item-comment"
            onClick={() => {
              if (comment.length && state.id) {
                addComment();
              }
            }}
            sx={{
              cursor: 'pointer',
              fontSize: '1.5rem',
              color:
                comment.length && state.id
                  ? customColors.BLUE
                  : customColors.SECONDARY_LIGHT,
            }}
          >
            send_rounded
          </Icon>
        ) : (
          <Button
            testid="action-item-comment"
            onClick={() => {
              addComment();
            }}
            size="sm"
            disabled={!comment?.length || !state.id}
            className={styles.sendButton}
            value={t(keys.action_items.SEND)}
          />
        )}
      </div>
      <div className={styles.commentContainer}>
        {activity?.length ? (
          <div className={styles.commentList}>
            {activity.map((event) =>
              event.__typename === 'ActionItemComment' ? (
                <ActionItemComment
                  key={`${event.__typename}:${event.id}`}
                  comment={event}
                  refetch={refetch}
                />
              ) : (
                <ActionItemHistory
                  key={`${event.__typename}:${event.id}`}
                  event={event}
                />
              ),
            )}
          </div>
        ) : null}
        <div ref={commentsEndRef} />
      </div>
    </div>
  );
}

const Attachments = ({ attachments, state, readOnly }) => {
  const [hideExtras, setHideExtras] = useState(true);

  const { amountHidden, attachmentsArray } = useMemo(() => {
    if (attachments.length > 2 && hideExtras) {
      return {
        amountHidden: attachments?.length - 2,
        attachmentsArray: attachments.slice(0, 2),
      };
    } else {
      return {
        amountHidden: 0,
        attachmentsArray: attachments,
      };
    }
  }, [attachments, hideExtras]);

  const [deleteAttachment] = useMutation(deleteAttachmentMutation);
  return (
    <div>
      <Spacer />
      <div className={styles.attachmentList}>
        {attachmentsArray.map((attachment, index) => {
          const file = {
            ...attachment,
            downloadAllowed: true,
            fileType: attachment.extension,
            url: attachment.url,
          };
          return (
            <div>
              <SimpleFileCard
                key={`${attachment.id}-${index}`}
                file={file}
                fileName={attachment.name}
                extension={attachment.extension}
                onDelete={() => {
                  deleteAttachment({
                    variables: {
                      attachmentId: attachment.id,
                      actionItemId: state?.id,
                    },
                  });
                }}
                readOnly={readOnly}
              />
            </div>
          );
        })}
      </div>
      <Spacer />
      {attachments.length > 2 && (
        <Text
          className={styles.showMoreAttachments}
          noMargin
          color="accentPrimary"
          size="sm"
          textAlign="right"
          weight="semiBold"
          onClick={() => setHideExtras(!hideExtras)}
        >
          {hideExtras
            ? t(keys.action_items.HIDDEN_ATTACHMENTS, { count: amountHidden })
            : t(keys.action.HIDE)}
        </Text>
      )}
    </div>
  );
};

export default function ActionItemModal({
  id = null,
  show = false,
  onHide,
  onComplete = () => {},
  fromInspection = false,
}) {
  const { simpleAssessmentModal } = useFlags();
  const { workspace, isWorkspaceAdmin, workspaceVehicles } = useWorkspace();
  const toastProps = useReactiveVar(toastVar);
  const emptyState = {
    title: '',
    description: '',
    priority: 'LOW',
    status: 'TODO',
    comments: [],
    labels: [],
    attachments: [],
    participants: [],
    watchers: [],
    workspace: { label: workspace?.title, value: workspace?.id, ...workspace },
    private: true,
    dueDate: null,
  };

  // Hooks
  const { deleteParam, upsertParam } = useQueryParams();
  const { user, observationName, incidentName, assessmentName, isAdmin } =
    useCurrentUser();

  const { openConfirmationModal, closeModal } = useModal();
  const commentsEndRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { FollowButton } = useWatcher();
  const {
    availableWorkspaces,
    companyPermissions,
    refetchWorkspacePermission,
    allWorkspaces,
  } = useWorkspace();
  const { addParticipant: addIncidentParticipant } = useIncidentMutations();
  const { addParticipant: addObservationParticipant } =
    useObservationMutations();
  const { companyAdmins } = useWorkspacePermissions();
  const { addUser } = useWorkspaceMutations();

  // State
  const [state, setState] = useState(emptyState);
  const [comment, setComment] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [closeLabels, setCloseLabels] = useState(false);
  const [editingDate, setEditingDate] = useState(!state.dueDate || !state.id);
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState(false);
  const [isEditingWorkspace, setIsEditingWorkspace] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(
    state?.id ? state.workspace || null : workspace.id,
  );

  const { changeWorkspace } = useChangeWorkspace();

  useEffect(() => {
    if (!state?.id && state.workspace?.id !== selectedWorkspace?.id) {
      setSelectedWorkspace(workspace || null);
    } else {
      setSelectedWorkspace(state.workspace);
    }
  }, [
    selectedWorkspace?.id,
    state?.id,
    state?.workspace,
    workspace,
    workspace?.id,
  ]);

  useEffect(() => {
    if (user.id === state?.assignee?.id && !state?.outcome) {
      setEditingOutcome(true);
    }
  }, [state, state?.assignee, user]);

  // Mutations
  const [deleteActionItem] = useMutation(deleteActionItemMutation, {
    update(cache, { data }) {
      if (data && data.deleteActionItem) {
        const deletedItemId = data.deleteActionItem.id;
        cache.modify({
          fields: {
            actionItems(existingActionItems = [], { readField }) {
              return existingActionItems?.filter(
                (actionItemRef) =>
                  deletedItemId !== readField('id', actionItemRef),
              );
            },
          },
        });
      }
    },
  });
  const [addActionItemLabel] = useMutation(addActionItemLabelMutation);
  const [deleteActionItemLabel] = useMutation(deleteActionItemLabelMutation);
  const [createComment] = useMutation(commentMutation);
  const [addActionItem] = useMutation(actionItemCreateMutation);
  const [updateActionItem] = useMutation(actionItemUpdateMutation);
  const [addParticipant] = useMutation(addParticipantMutation);
  const [removeParticipant] = useMutation(removeParticipantMutation);
  const [addAttachment] = useMutation(addAttachmentMutation);

  // Queries
  const { data, refetch } = useQuery(actionItemQuery, {
    skip: isNaN(id) || !show,
    fetchPolicy: 'network-only',
    variables: {
      id: `${id}`,
    },
  });

  // useMemos
  const activity = useMemo(() => {
    const activityEvents = data?.events || [];
    const activityComments = state?.comments || [];

    const mappedEvents = activityEvents.map((event) => ({
      ...event,
      moment: getLocalTime(event.dateCreated),
    }));

    const mappedComments = activityComments.map((comment) => ({
      ...comment,
      moment: getLocalTime(comment.dateCreated),
    }));

    const mergedArray = [
      ...(showDetails ? mappedEvents : []),
      ...mappedComments,
    ];

    mergedArray.sort((a, b) => a.moment - b.moment);

    return mergedArray;
  }, [data?.events, state?.comments, showDetails]);
  const isCreator = user.id === state?.creator?.id;
  const isAssignee = user.id === state?.assignee?.id;
  const isParticipant = state?.participants?.some((p) => p.id === user.id);
  const isComplete = state?.status === statusStringLookup.DONE.key;
  const isMobile = useIsMobile();

  const readOnly = useMemo(() => {
    if (!id || !data?.actionItems?.length || !user?.id) {
      return false;
    }
    if (
      !isComplete &&
      (isWorkspaceAdmin || isCreator || isAssignee || isParticipant)
    ) {
      return false;
    }

    return true;
  }, [
    id,
    data?.actionItems?.length,
    user?.id,
    isComplete,
    isWorkspaceAdmin,
    isCreator,
    isAssignee,
    isParticipant,
  ]);

  const canSave = useMemo(() => {
    return state.title && state.title !== 'Title';
  }, [state.title]);

  const isDraft = useMemo(() => !state?.id, [state]);

  useEffect(() => {
    if (!readOnly && !id) {
      setIsEditingDescription(true);
      setIsEditingTitle(true);
    } else {
      setIsEditingDescription(false);
      setIsEditingTitle(false);
    }

    if (fromInspection) {
      setIsEditingDescription(true);
    }
  }, [id, readOnly, show, fromInspection]);

  useEffect(() => {
    if (!data?.actionItems?.length) {
      return;
    }
    const {
      actionItems: [actionItem],
    } = data;
    setState(actionItem);
  }, [data]);

  const isIncidentParticipant = useMemo(() => {
    const participants = state?.incident?.participants?.map((p) => p.id) || [];
    if (
      user.id === state?.incident?.creator?.id ||
      participants.includes(user.id) ||
      isAdmin
    ) {
      return true;
    }
    return false;
  }, [
    isAdmin,
    state?.incident?.creator?.id,
    state?.incident?.participants,
    user.id,
  ]);

  const isObservationParticipant = useMemo(() => {
    const participants =
      state?.observation?.participants?.map((p) => p?.id) || [];
    if (
      user?.id === state?.observation?.creator?.id ||
      participants?.includes(user?.id) ||
      isAdmin
    ) {
      return true;
    }
    return false;
  }, [
    isAdmin,
    state?.observation?.creator?.id,
    state?.observation?.participants,
    user?.id,
  ]);

  const links = useMemo(() => {
    const padId = (id) => id?.toString().padStart(4, '0');
    const {
      incident,
      correctiveAction,
      riskRegistry,
      inspection,
      observation,
    } = state || {};
    return [
      ...(incident?.id && isIncidentParticipant
        ? [
            {
              key: 'incident',
              type: incidentName || t(keys.common.INCIDENT),
              label: incident.externalId || padId(incident.id),
              path: `${paths.incident}/${incident.id}`,
            },
          ]
        : []),
      ...(correctiveAction?.id
        ? [
            {
              key: 'correctiveAction',
              type: 'Corrective Action',
              label: correctiveAction.externalId || padId(correctiveAction.id),
              path: `/${paths.cpa}/${correctiveAction.id}`,
            },
          ]
        : []),
      ...(riskRegistry?.id
        ? [
            {
              key: 'riskRegistry',
              type: t(keys.common.RISK_REGISTRY),
              label:
                riskRegistry.category ||
                riskRegistry.description ||
                'Untitled Risk',
              path: `/${paths.registry}/${riskRegistry.id}?section=DETAILS`,
            },
          ]
        : []),
      ...(inspection?.id
        ? [
            {
              key: 'inspection',
              type: assessmentName || t(keys.common.ASSESSMENT),
              label: inspection.template?.title,
              path: getRoute(
                state?.workspace?.id,
                paths.assessment,
                inspection.id,
              ),
            },
          ]
        : []),
      ...(observation?.id && isObservationParticipant
        ? [
            {
              key: 'observation',
              type: observationName || t(keys.common.OBSERVATION),
              label: observation.externalId || padId(observation.id),
              path: `/${paths.observation}/${observation.id}`,
            },
          ]
        : []),
    ];
  }, [
    state,
    isIncidentParticipant,
    isObservationParticipant,
    incidentName,
    assessmentName,
    observationName,
    t,
  ]);

  // util functions
  const addComment = () => {
    createComment({
      variables: {
        actionItemId: state.id,
        text: comment,
      },
    }).then(() => {
      refetch({ id: `${state.id}` }).then(() => {
        setComment('');
      });
    });
  };

  const onClose = () => {
    deleteParam(searchParamKeys.actionItem);
    onComplete();
    setIsEditingDescription(false);
    setIsEditingTitle(false);
    setComment('');
    setState(emptyState);
    setEditingOutcome(false);
    onHide();
    deleteParam(searchParamKeys.actionItem);
    deleteParam('allWorkspaces');
    setEditingDate(true);
  };
  const showUsers = useMemo(
    () =>
      selectedWorkspace?.id || !!state.workspace?.id ? 'workspace' : 'all',
    [selectedWorkspace?.id, state.workspace?.id],
  );

  const titleRef = useRef(null);
  const descriptionRef = useRef(null);

  useEffect(() => {
    if (isEditingTitle && !!titleRef.current) {
      titleRef.current.focus();
    }
    if (isEditingDescription && !!descriptionRef.current) {
      descriptionRef.current.focus();
    }
  }, [isEditingDescription, isEditingTitle]);

  const addParticipantToIncident = (userId) => {
    const participants = state?.incident?.participants?.map((p) => p.id) || [];
    if (!participants.includes(userId)) {
      addIncidentParticipant({ userId, incidentId: state.incident.id });
    }
  };
  const addParticipantToObservation = (userId) => {
    const participants =
      state?.observation?.participants?.map((p) => p.id) || [];

    if (!participants.includes(userId)) {
      addObservationParticipant({
        participants: [userId],
        observationId: state.observation.id,
      });
    }
  };
  const participantAccess = useMemo(() => {
    const allWorkspaceUsers = companyPermissions
      ?.filter((c) => c.workspaceId === state?.workspace?.id)
      ?.map((p) => p.user);
    const workspaceUsers = [...companyAdmins, ...allWorkspaceUsers];
    return new Set(workspaceUsers.map((user) => user.id));
  }, [companyPermissions, companyAdmins, state?.workspace?.id]);

  return show ? (
    <div className={styles.show}>
      <div className={styles.background}>
        <Toast {...toastProps} onHide={hideToast} />
        <div
          className={classNames(
            styles.container,
            show ? styles.showContainer : styles.hideContainer,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.mainHeader}>
            <div className={styles.titleContainer}>
              {isEditingTitle ? (
                <Form.Control
                  className={styles.titleInput}
                  type="text"
                  required
                  data-cy="input-action-item-title"
                  placeholder={t(keys.action_items.TITLE_PLACEHOLDER)}
                  value={state.title}
                  ref={titleRef}
                  onChange={(e) =>
                    setState({
                      ...state,
                      title: e.target.value,
                    })
                  }
                  onBlur={() => {
                    if (state.id) {
                      updateActionItem({
                        variables: {
                          id: state.id,
                          title: state.title,
                        },
                      });
                    }
                  }}
                />
              ) : (
                <Text size="lg" noSelect weight="bold" noMargin>
                  {state.title}
                </Text>
              )}
              {(isCreator || isWorkspaceAdmin) && !isComplete && (
                <Icon
                  baseClassName="material-icons-outlined"
                  data-cy="action-item-title-edit"
                  className={styles.iconButton}
                  onClick={() => {
                    setIsEditingTitle(!isEditingTitle);
                  }}
                  sx={{ fontSize: '1.2rem' }}
                >
                  {isEditingTitle ? 'check' : 'edit'}
                </Icon>
              )}
            </div>
            <Icon
              baseClassName="material-icons-outlined"
              className={styles.closeIcon}
              data-cy="close-action-item-modal"
              onClick={() => {
                setCloseLabels(true);
                if (!state.id) {
                  openConfirmationModal({
                    title: t(keys.action_items.CLOSE_ACTION_ITEM),
                    description: t(keys.action_items.CLOSE_ACTION_ITEM_MESSAGE),
                    variant: 'danger',
                    buttonText: t(keys.action_items.CLOSE),
                    onSubmit: () => {
                      onClose();
                    },
                  });
                } else {
                  onClose();
                }
              }}
            >
              close
            </Icon>
          </div>
          <div className={styles.splitContainer}>
            <div className={styles.leftContainer}>
              <div className={styles.header}>
                {!simpleAssessmentModal ? (
                  <div className={styles.subButtonContainer}>
                    <div className={styles.watchButton}>
                      <FollowButton
                        type={'actionItem'}
                        id={state?.id}
                        userId={user?.id}
                        disabled={isDraft}
                      />
                    </div>

                    <div className={styles.privateCheck}>
                      <Form.Check
                        disabled={isDraft || readOnly || !state.workspace?.id}
                        onChange={(e) => {
                          if (state?.id) {
                            state?.private
                              ? openConfirmationModal({
                                  title: t(keys.action_items.MAKE_PUBLIC_TITLE),
                                  description: t(
                                    keys.action_items.MAKE_PUBLIC_TEXT,
                                  ),
                                  variant: 'warning',
                                  buttonText: t(keys.action.CONFIRM),
                                  onSubmit: () => {
                                    updateActionItem({
                                      variables: {
                                        id: state.id,
                                        private: false,
                                      },
                                    });
                                  },
                                })
                              : updateActionItem({
                                  variables: {
                                    id: state.id,
                                    private: e.target.checked,
                                  },
                                });
                          } else {
                            setState({ ...state, private: e.target.checked });
                          }
                        }}
                        checked={state?.private}
                      />
                      <Text
                        noMargin
                        weight="semiBold"
                        color={
                          isDraft || readOnly || !state.workspace
                            ? 'secondaryLight'
                            : 'regular'
                        }
                      >
                        {t(keys.action_items.PRIVATE_TITLE)}
                      </Text>
                    </div>
                  </div>
                ) : (
                  <Spacer size="sm" />
                )}
                <div className={styles.flexRowRight}>
                  <div className={styles.rightSection}>
                    <DropdownButton
                      disabled={
                        (!isAssignee && !isCreator && !isWorkspaceAdmin) ||
                        !state?.id
                      }
                      size="sm"
                      className={styles.dropdown}
                      title={t(statusStringLookup[state.status].title)}
                      variant={statusStringLookup[state.status].variant}
                      onSelect={(val) => {
                        setState({ ...state, status: val });
                        if (state.id) {
                          updateActionItem({
                            variables: {
                              id: state.id,
                              status: val,
                            },
                          });
                        }
                      }}
                    >
                      <Dropdown.Item
                        className={styles.dropdown}
                        eventKey="BACKLOG"
                      >
                        {t(keys.common.BACKLOG)}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className={styles.dropdown}
                        eventKey="TODO"
                      >
                        {t(keys.common.TODO)}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className={styles.dropdown}
                        eventKey="IN_PROGRESS"
                      >
                        {t(keys.common.IN_PROGRESS)}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className={styles.dropdown}
                        onClick={() => setEditingOutcome(true)}
                        eventKey="DONE"
                      >
                        {t(keys.common.COMPLETE)}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className={styles.dropdown}
                        eventKey="DUPLICATE"
                      >
                        {t(keys.action_items.DUPLICATE)}
                      </Dropdown.Item>
                    </DropdownButton>
                  </div>
                  <div className={styles.rightSection}>
                    <DropdownButton
                      size="sm"
                      disabled={readOnly}
                      title={t(priorityStringLookup[state.priority].title)}
                      variant={
                        state.priority === 'LOW'
                          ? 'success'
                          : state.priority === 'MEDIUM'
                          ? 'warning'
                          : 'danger'
                      }
                      id="dropdown-basic"
                      className={styles.dropdown}
                      onSelect={(val) => {
                        setState({
                          ...state,
                          priority: val,
                        });
                        if (state.id) {
                          updateActionItem({
                            variables: {
                              id: state.id,
                              priority: val,
                            },
                          });
                        }
                      }}
                    >
                      <Dropdown.Item
                        className={styles.dropdown}
                        eventKey="HIGH"
                      >
                        {t(keys.action_items.HIGH)}
                      </Dropdown.Item>
                      <Dropdown.Item
                        className={styles.dropdown}
                        eventKey="MEDIUM"
                      >
                        {t(keys.action_items.MEDIUM)}
                      </Dropdown.Item>
                      <Dropdown.Item className={styles.dropdown} eventKey="LOW">
                        {t(keys.action_items.LOW)}
                      </Dropdown.Item>
                    </DropdownButton>
                  </div>
                </div>
              </div>
              {!!state.id && !simpleAssessmentModal && (
                <Text size="sm" color="secondary" noMargin>
                  {!state.workspace
                    ? t(keys.action_items.SHARED)
                    : !!state.private
                    ? t(keys.action_items.PRIVATE_TEXT)
                    : t(keys.action_items.PUBLIC)}
                </Text>
              )}
              {simpleAssessmentModal && isMobile && (
                <div className={styles.rightSection}>
                  <Text weight="semiBold" noSelect noMargin>
                    {t(keys.common.ASSIGNEE)}
                  </Text>
                  <div>
                    {!readOnly ? (
                      <UserSelector
                        userQuery={state.workspace?.id ? 'workspace' : 'all'}
                        className={styles.fullWidth}
                        cardProps={{ weight: 'regular', largeAvatar: true }}
                        otherWorkspace={
                          selectedWorkspace ||
                          state.incident?.workspace ||
                          state.observation?.workspace ||
                          state?.workspace
                        }
                        type={showUsers}
                        selected={state.assignee}
                        onClose={() => {
                          setState({ ...state, assignee: undefined });
                          if (state?.id) {
                            updateActionItem({
                              variables: { id: state.id, assigneeId: null },
                            });
                          }
                        }}
                        onSelectionChanged={(user) => {
                          setState({
                            ...state,
                            assigneeId: user ? user.id : null,
                            assignee: user,
                          });
                          if (state.id) {
                            updateActionItem({
                              variables: {
                                id: state.id,
                                assigneeId: user ? user.id : 0,
                              },
                            }).then(() => {
                              if (state.type === 'INCIDENT') {
                                addParticipantToIncident(user.id);
                              }
                              if (state.type === 'OBSERVATION') {
                                addParticipantToObservation(user.id);
                              }
                            });
                          }
                        }}
                        usersToLeaveOut={state?.participants}
                      />
                    ) : state.assignee ? (
                      <SimpleUserCard
                        weight="regular"
                        user={state.assignee}
                        mediumAvatar
                      />
                    ) : (
                      <Text noMargin>{t(keys.common.NONE)}</Text>
                    )}
                  </div>
                </div>
              )}
              <div className={styles.section}>
                {!isMobile && (
                  <Icon baseClassName="material-icons-outlined">
                    description
                  </Icon>
                )}
                <div className={styles.sectionContent}>
                  <div className={styles.subheaderContainer}>
                    <Text size="md" weight={'semiBold'} noMargin>
                      {t(keys.common.DESCRIPTION)}
                    </Text>
                    {(isWorkspaceAdmin || isCreator) && !isComplete ? (
                      <Icon
                        baseClassName="material-icons-outlined"
                        className={styles.iconButton}
                        data-cy="action-item-description-edit"
                        onClick={() => {
                          setIsEditingDescription(!isEditingDescription);
                        }}
                        sx={{ fontSize: '1.2rem' }}
                      >
                        {isEditingDescription ? 'check' : 'edit'}
                      </Icon>
                    ) : null}
                  </div>
                  <div className={styles.leftSection}>
                    {isEditingDescription ? (
                      <Form.Control
                        className={styles.descriptionInput}
                        as="textarea"
                        rows={5}
                        data-cy="input-action-item-description"
                        placeholder={t(
                          keys.action_items.DESCRIPTION_PLACEHOLDER,
                        )}
                        value={state.description}
                        ref={descriptionRef}
                        onChange={(e) =>
                          setState({
                            ...state,
                            description: e.target.value,
                          })
                        }
                        onBlur={() => {
                          if (state.id) {
                            updateActionItem({
                              variables: {
                                id: state.id,
                                description: state.description,
                              },
                            });
                          }
                        }}
                      />
                    ) : !!state.description ? (
                      <div className={styles.readOnlyDescription}>
                        <Text
                          prewrap
                          noMargin
                          color={
                            !!state.description ? 'semiBold' : 'secondaryLight'
                          }
                        >
                          {state.description}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className={styles.leftBottom}>
                {!isDraft && !simpleAssessmentModal && (
                  <div className={styles.section}>
                    {!isMobile && <Icon>checklist</Icon>}
                    <div className={styles.sectionContent}>
                      <ActionItemChecklist
                        refetchActionItem={refetch}
                        actionItem={state}
                        refetch={refetch}
                        readOnly={
                          (!isCreator && !isWorkspaceAdmin && !isParticipant) ||
                          isComplete
                        }
                      />
                    </div>
                  </div>
                )}
                {!!state.id && !!state.assignee && (
                  <>
                    <div className={styles.section}>
                      <Icon>task_alt</Icon>
                      <div className={styles.sectionContent}>
                        <div className={styles.subheaderContainer}>
                          <Text size="md" weight="semiBold" noSelect noMargin>
                            Action Taken by Assignee
                            <span className={styles.red}>*</span>
                          </Text>
                          {!readOnly && (
                            <Icon
                              sx={{ fontSize: '1.2rem' }}
                              className={styles.iconButton}
                              baseClassName="material-icons-outlined"
                              onClick={() => {
                                setEditingOutcome(!editingOutcome);
                              }}
                            >
                              {editingOutcome ? 'check' : 'edit'}
                            </Icon>
                          )}
                        </div>
                        <Spacer />
                        {!isComplete &&
                        (editingOutcome ||
                          (isAssignee && !state.outcome && !isComplete)) ? (
                          <Form.Control
                            as="textarea"
                            value={state?.outcome ?? ''}
                            rows={5}
                            onChange={(e) => {
                              setState({ ...state, outcome: e.target.value });
                            }}
                            onBlur={() =>
                              updateActionItem({
                                variables: {
                                  id,
                                  outcome: state.outcome,
                                  status:
                                    state.status === statusStringLookup.TODO.key
                                      ? statusStringLookup.IN_PROGRESS.key
                                      : undefined,
                                },
                              })
                            }
                          />
                        ) : state.outcome ? (
                          <div className={styles.readOnlyDescription}>
                            <Text
                              prewrap
                              noMargin
                              color={
                                !!state.outcome ? 'semiBold' : 'secondaryLight'
                              }
                            >
                              {state.outcome}
                            </Text>
                          </div>
                        ) : null}
                        {!!state.outcome && !readOnly && (
                          <Button
                            size="sm"
                            align={'right'}
                            disabled={!state.outcome}
                            value={t(keys.common.COMPLETE)}
                            icon="check_circle"
                            variant="success"
                            onClick={() => {
                              updateActionItem({
                                variables: {
                                  id: state.id,
                                  status: statusStringLookup.DONE.key,
                                },
                              });
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
                {!isDraft && (
                  <div className={styles.section}>
                    {!isMobile ? <Icon>attachment</Icon> : null}
                    <div className={styles.sectionContent}>
                      <div className={styles.header}>
                        <Text size="md" weight="semiBold" noSelect noMargin>
                          {t(keys.common.ATTACHMENTS)}
                        </Text>
                        <div className={styles.attachmentContainer}>
                          {!readOnly && (
                            <QuickFileDropzone
                              iconSize={'1.5rem'}
                              setLoading={setLoadingAttachment}
                              className={styles.fileDrop}
                              loading={loadingAttachment}
                              icon={true}
                              iconText={''}
                              size="md"
                              onSubmit={({ url, name, extension }) =>
                                addAttachment({
                                  variables: {
                                    actionItemId: state.id,
                                    url,
                                    extension,
                                    name,
                                  },
                                })
                              }
                            />
                          )}
                        </div>
                      </div>
                      {state?.attachments?.length > 0 ? (
                        <Attachments
                          attachments={state?.attachments}
                          readOnly={readOnly}
                          state={state}
                        />
                      ) : null}
                    </div>
                  </div>
                )}
                {!isDraft && (
                  <>
                    <div className={styles.section}>
                      {!isMobile ? (
                        <Icon baseClassName="material-icons-outlined">
                          chat
                        </Icon>
                      ) : null}
                      <div className={styles.sectionContent}>
                        <div className={styles.subheaderContainer}>
                          <Text size="md" weight="semiBold" noMargin noSelect>
                            {`${t(keys.action_items.ACTIVITY)} ${
                              activity.length ? `(${activity.length})` : ''
                            }`}
                          </Text>
                          <Text
                            size="sm"
                            className={styles.link}
                            weight="semiBold"
                            color="accentPrimary"
                            noMargin
                            noSelect
                            onClick={() => {
                              setShowDetails(!showDetails);
                            }}
                          >
                            {showDetails
                              ? t(keys.action.HIDE_VARIABLE, {
                                  variable: t(keys.common.DETAILS),
                                })
                              : t(keys.action.SHOW_VARIABLE, {
                                  variable: t(keys.common.DETAILS),
                                })}
                          </Text>
                        </div>
                        {state.id && (
                          <div className={styles.leftSection}>
                            <Comments
                              state={state}
                              activity={activity}
                              showDetails={showDetails}
                              setShowDetails={setShowDetails}
                              comment={comment}
                              commentsEndRef={commentsEndRef}
                              addComment={addComment}
                              setComment={setComment}
                              user={user}
                              refetch={refetch}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.rightContainer}>
              <div className={styles.rightSection}>
                <Text weight="semiBold" noSelect noMargin>
                  {t(keys.common.WORKSPACE)}
                </Text>
                {!state.id || isEditingWorkspace ? (
                  <div className={styles.fullWidth}>
                    <OPASDropdown
                      isClearable={false}
                      type="workspace"
                      className={styles.selector}
                      selected={selectedWorkspace}
                      placeholder="Select Workspace"
                      options={availableWorkspaces}
                      onChange={(ws) => {
                        if (!!ws?.id) {
                          changeWorkspace(ws?.id);
                        }
                        const newWS =
                          allWorkspaces?.find((w) => w.id === ws?.value) ||
                          null;
                        setSelectedWorkspace(newWS);
                        setState((prev) => ({
                          ...prev,
                          workspace: newWS,
                          private: !ws?.value ? true : prev.private,
                        }));
                        if (state.id) {
                          if (ws?.value) {
                            updateActionItem({
                              variables: {
                                id: state.id,
                                workspaceId: ws.value,
                              },
                            });
                          } else {
                            updateActionItem({
                              variables: {
                                id: state.id,
                                workspaceId: null,
                                private: true,
                              },
                            });
                          }
                        }
                        setIsEditingWorkspace(false);
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.flexRow}>
                    <SimpleWorkspaceCard
                      mediumAvatar
                      weight="regular"
                      workspace={state.workspace}
                    />
                    {(isCreator || isWorkspaceAdmin) && !isComplete && (
                      <Icon
                        onClick={() => setIsEditingWorkspace(true)}
                        baseClassName="material-icons-outlined"
                        style={{
                          fontSize: '1.2rem',
                          color: customColors.SECONDARY,
                        }}
                      >
                        edit
                      </Icon>
                    )}
                  </div>
                )}
              </div>
              <div className={styles.rightSection}>
                <Text weight="semiBold" noSelect noMargin>
                  {t(keys.common.CREATOR)}
                </Text>
                <SimpleUserCard
                  largeAvatar
                  user={state.creator}
                  weight="regular"
                />
              </div>

              {(!simpleAssessmentModal || !isMobile) && (
                <div className={styles.rightSection}>
                  <Text weight="semiBold" noSelect noMargin>
                    {t(keys.common.ASSIGNEE)}
                  </Text>
                  <div>
                    {!readOnly ? (
                      <UserSelector
                        userQuery={state.workspace?.id ? 'workspace' : 'all'}
                        className={styles.fullWidth}
                        cardProps={{ weight: 'regular', largeAvatar: true }}
                        otherWorkspace={
                          selectedWorkspace ||
                          state.incident?.workspace ||
                          state.observation?.workspace ||
                          state?.workspace
                        }
                        type={showUsers}
                        selected={state.assignee}
                        onClose={() => {
                          setState({ ...state, assignee: undefined });
                          if (state?.id) {
                            updateActionItem({
                              variables: { id: state.id, assigneeId: null },
                            });
                          }
                        }}
                        onSelectionChanged={(user) => {
                          setState({
                            ...state,
                            assigneeId: user ? user.id : null,
                            assignee: user,
                          });
                          if (state.id) {
                            updateActionItem({
                              variables: {
                                id: state.id,
                                assigneeId: user ? user.id : 0,
                              },
                            }).then(() => {
                              if (state.type === 'INCIDENT') {
                                addParticipantToIncident(user.id);
                              }
                              if (state.type === 'OBSERVATION') {
                                addParticipantToObservation(user.id);
                              }
                            });
                          }
                        }}
                        usersToLeaveOut={state?.participants}
                      />
                    ) : state.assignee ? (
                      <SimpleUserCard
                        weight="regular"
                        user={state.assignee}
                        mediumAvatar
                      />
                    ) : (
                      <Text noMargin>{t(keys.common.NONE)}</Text>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.rightSection}>
                <Text
                  noSelect
                  weight="semiBold"
                  noMargin
                  className={styles.subtitle}
                >
                  {t(keys.common.PARTICIPANTS)}
                </Text>
                {!isDraft ? (
                  !readOnly ? (
                    <UserMultiSelector
                      text="regular"
                      type={showUsers}
                      showUserAlert={true}
                      parentId={state?.id}
                      parentType={'actionItem'}
                      setUsers={(participants) =>
                        setState((prev) => ({ ...prev, participants }))
                      }
                      otherWorkspace={selectedWorkspace || state?.workspace}
                      readOnly={readOnly}
                      selected={state?.participants}
                      onUserAdded={(user) =>
                        addParticipant({
                          variables: {
                            actionItemId: state?.id,
                            userId: user?.id,
                          },
                        }).then(() => {
                          if (!!state?.incident?.id) {
                            addParticipantToIncident(user.id);
                          } else if (!!state?.observation?.id) {
                            addParticipantToObservation(user.id);
                          }
                        })
                      }
                      onUserRemoved={(user) =>
                        removeParticipant({
                          variables: {
                            actionItemId: state?.id,
                            userId: user?.id,
                          },
                        })
                      }
                      usersToLeaveOut={state?.assignee ? [state?.assignee] : []}
                    />
                  ) : state?.participants?.length > 0 ? (
                    <div className={styles.participants}>
                      {state?.participants.map((user) => {
                        const isInWorkspace =
                          participantAccess?.has(user.id) ||
                          !state.workspace?.id;
                        return (
                          <div className={styles.flexRow}>
                            <SimpleUserCard user={user} weight="regular" />
                            {!isInWorkspace ? (
                              <Tooltip
                                title={`No Access to ${state.workspace?.title} `}
                              >
                                <Icon
                                  style={{
                                    color: customColors.RED_LIGHT,
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                  }}
                                  onClick={() => {
                                    if (isAdmin || isWorkspaceAdmin) {
                                      openConfirmationModal({
                                        variant: 'warning',
                                        buttonText: 'Add to Workspace',
                                        title: `User Workspace Permission`,
                                        description: `${user.firstName} ${user.lastName} does not have workspace permissions for ${state.workspace?.title} and will not be able to view this Action Item. Would you like to add them?`,
                                        onSubmit: () => {
                                          addUser({
                                            userId: user.id,
                                            workspaceId: state.workspace?.id,
                                          }).then(() =>
                                            refetchWorkspacePermission(),
                                          );
                                        },
                                      });
                                    }
                                  }}
                                >
                                  error
                                </Icon>
                              </Tooltip>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Text noMargin>{t(keys.common.NONE)}</Text>
                  )
                ) : (
                  <Text noMargin color="secondary" size="sm">
                    {t(keys.action_items.CREATION_PARTICIPANTS)}
                  </Text>
                )}
              </div>
              <div className={styles.rightSection}>
                <div className={styles.dueDate}>
                  <Text
                    weight="semiBold"
                    noSelect
                    noMargin
                    className={styles.subtitle}
                  >
                    {t(keys.action_items.DUE_DATE)}
                  </Text>
                  <div>
                    {!editingDate || isComplete ? (
                      <div className={styles.header}>
                        <Text
                          noMargin
                          weight="semiBold"
                          size="md-lg"
                          color={checkDueDateColor({
                            dueDate: state.dueDate,
                            status: state.status || 'TODO',
                          })}
                        >
                          {state?.dueDate
                            ? getLocalTime(state.dueDate).format('MMM D, YYYY')
                            : t(keys.common.NONE)}
                        </Text>
                        {!readOnly && (
                          <Icon
                            className={styles.iconButton}
                            baseClassName="material-icons-outlined"
                            sx={{ fontSize: '1.2rem' }}
                            onClick={() => setEditingDate(!editingDate)}
                          >
                            edit
                          </Icon>
                        )}
                      </div>
                    ) : (
                      <div className={styles.header}>
                        <DatePickerComponent
                          disabled={readOnly}
                          selected={state.dueDate}
                          editing={editingDate}
                          setEditing={setEditingDate}
                          onChange={(newDate) => {
                            const endOfDay = moment(parseInt(newDate))?.endOf(
                              'day',
                            );
                            if (state?.id) {
                              updateActionItem({
                                variables: {
                                  id: state.id,
                                  dueDate: endOfDay?.toISOString(),
                                },
                              });
                            }
                            setState({
                              ...state,
                              dueDate: endOfDay
                                ? String(endOfDay.valueOf())
                                : null,
                            });
                          }}
                        />
                        {!readOnly && !!state.dueDate && (
                          <Icon
                            className={styles.iconButton}
                            baseClassName="material-icons-outlined"
                            sx={{
                              fontSize: '1.2rem',
                              color: customColors.GREEN,
                            }}
                            onClick={() => setEditingDate(!editingDate)}
                          >
                            check
                          </Icon>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {links.length > 0 && (
                <div className={styles.rightSection}>
                  <Text weight="semiBold" noSelect noMargin>
                    {`${t(keys.common.LINK)}`}
                  </Text>
                  {links.map(({ key, type, label, path }) => (
                    <div key={key}>
                      <Text
                        size="sm"
                        color="secondary"
                        weight="semiBold"
                        noMargin
                        className={styles.subtitle}
                      >
                        {type}
                      </Text>
                      <TextWithIcon
                        link
                        color="accentPrimary"
                        weight="semiBold"
                        size="md-lg"
                        noMargin
                        hover
                        onClick={() => {
                          onClose();
                          navigate(path);
                        }}
                      >
                        {label}
                      </TextWithIcon>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.rightSection}>
                <div className={styles.addAssetButton}>
                  <Text weight="semiBold" noMargin className={styles.subtitle}>
                    {t(keys.common.ASSET)}
                  </Text>
                  {!readOnly && !state.vehicle?.id && (
                    <Icon
                      onClick={() => setAssetModalOpen(true)}
                      color="primary"
                      className={styles.hover}
                    >
                      add
                    </Icon>
                  )}
                </div>

                {!!state?.vehicle ? (
                  <div className={styles.linkContainer}>
                    <Text
                      color="accentPrimary"
                      weight="semiBold"
                      noMargin
                      size="md-lg"
                      hover
                      onClick={() => {
                        onClose();
                        navigate(
                          getRoute(
                            state?.workspace?.id,
                            paths.asset,
                            state.vehicle?.id,
                          ),
                        );
                      }}
                    >
                      {`${state.vehicle?.make} ${state.vehicle?.model}
                          ${state.vehicle?.unitNumber || ''}`}
                    </Text>
                    {!readOnly && (
                      <Icon
                        baseClassName="material-icons-outlined"
                        onClick={() => {
                          if (state?.id) {
                            updateActionItem({
                              variables: { id: state.id, vehicleId: null },
                            });
                          } else {
                            setState({ ...state, vehicle: null });
                          }
                        }}
                        sx={{ fontSize: '1.4rem' }}
                        className={styles.hover}
                        color="primary"
                      >
                        close
                      </Icon>
                    )}
                  </div>
                ) : null}
                {assetModalOpen ? (
                  <AddAssetModal
                    onHide={() => setAssetModalOpen(false)}
                    show={assetModalOpen}
                    target={state}
                    targetKey={'id'}
                    title={t(keys.action_items.ADD_ASSET)}
                    addAsset={(asset) => {
                      if (state?.id) {
                        updateActionItem({
                          variables: { id: state.id, vehicleId: asset.id },
                        });
                      } else {
                        setState({ ...state, vehicle: asset });
                      }
                    }}
                    assets={workspaceVehicles}
                  />
                ) : null}
              </div>
              <div className={styles.rightSection}>
                <Text
                  weight="semiBold"
                  noSelect
                  noMargin
                  className={styles.subtitle}
                >
                  {t(keys.common.LABELS)}
                </Text>
                <LabelSelector
                  testId="action-item-modal"
                  readOnly={readOnly}
                  onClose={closeLabels}
                  setOnClose={setCloseLabels}
                  className={styles.labelBox}
                  selected={state.labels}
                  onSelectionChanged={(action, label) => {
                    if (action === 'ADD') {
                      const newLabels = [...state.labels, label].sort(
                        (a, b) => {
                          if (a.name < b.name) {
                            return -1;
                          }
                          if (a.name > b.name) {
                            return 1;
                          }
                          return 0;
                        },
                      );
                      setState({
                        ...state,
                        labels: newLabels,
                      });
                      if (state.id) {
                        addActionItemLabel({
                          variables: {
                            actionItemId: state.id,
                            labelId: label.id,
                          },
                        });
                      }
                    } else if (action === 'REMOVE') {
                      setState({
                        ...state,
                        labels: state.labels.filter((l) => l.id !== label.id),
                      });
                      if (state.id) {
                        deleteActionItemLabel({
                          variables: {
                            labelId: label.id,
                            actionItemId: state.id,
                          },
                        }).then(() => {
                          refetch();
                        });
                      }
                    }
                  }}
                />
              </div>
              {state.id && (
                <>
                  <Button
                    baseClassName="material-icons-outlined"
                    sx={{ color: customColors.RED }}
                    disabled={
                      !isWorkspaceAdmin && user?.id !== state.creator?.id
                    }
                    variant="danger"
                    outlined
                    onClick={() =>
                      openConfirmationModal({
                        title: t(keys.action.DELETE_VARIABLE, {
                          variable: t(keys.common.ACTION_ITEM),
                        }),
                        description: t(keys.action.DELETE_CONFIRMATION, {
                          variable: `${t(keys.common.ACTION_ITEM)} ${
                            state.title
                          }`,
                        }),
                        variant: 'danger',
                        onSubmit: () => {
                          deleteActionItem({
                            variables: { id: state.id },
                          }).then(() => {
                            setState(emptyState);
                            closeModal({ modalName: modals.confirmation });
                            onClose();
                            window.location.reload();
                          });
                          setTimeout(() => {
                            showToast({
                              title: state?.title,
                              message: t(keys.action.DELETED, {
                                variable: state?.title,
                              }),
                              variant: toastVariant.info,
                              time: toastLength.md,
                            });
                          }, 500);
                        },
                      })
                    }
                    icon="delete"
                    value={t(keys.common.DELETE, { variable: null })}
                  >
                    delete
                  </Button>
                  <br />
                  <br />
                  <br />
                </>
              )}
              {!state.id ? (
                <div className={styles.flexRowRight}>
                  {!state?.title && (
                    <Text
                      color="secondary"
                      textAlign="right"
                      size="sm"
                      noMargin
                    >
                      Please enter a title to continue
                    </Text>
                  )}
                  <Button
                    disabled={!canSave}
                    testId="action-item-modal"
                    value={t(keys.action.CREATE)}
                    onClick={() => {
                      addActionItem({
                        variables: {
                          ...state,
                          dueDate: state?.dueDate
                            ? getLocalTime(state.dueDate)?.utc()?.toISOString()
                            : null,
                          type: actionItemModalVar().type,
                          labels: state.labels.map((l) => l.id),
                          workspaceId: state?.workspace?.id,
                          vehicleId: state?.vehicle?.id,
                          assigneeId: state?.assignee?.id,
                        },
                      }).then(({ data }) => {
                        if (!data?.addActionItem?.id) {
                          showToast({
                            title: t(keys.action_items.OOPS),
                            message: t(keys.action_items.PROBLEM),
                            variant: 'danger',
                            time: toastLength.lg,
                          });
                        } else {
                          setState({
                            ...state,
                            id: data.addActionItem.id,
                          });

                          upsertParam(
                            searchParamKeys.actionItem,
                            data.addActionItem.id,
                          );

                          setCloseLabels(true);
                        }
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
